import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Snowflake, ChefHat, Globe2, Award, Utensils, Cpu, Truck } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { images, navPaths } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const stepIcons = [ChefHat, Snowflake, Truck, Cpu];

export default function Home() {
  const { t } = useLang();
  useReveal([t]);
  const h = t.home;

  return (
    <main>
      <Seo
        title="MichelChef® | Cucina italiana gourmet reinventata dall'innovazione"
        description="Startup food-tech: piatti gourmet italiani in monoporzione, prodotti da uno Chef e rigenerati ovunque con un brevetto che preserva aromi e sapori."
        path="/"
        image={images.hero}
      />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={images.hero} alt="Gourmet" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b0a]/95 via-[#0d0b0a]/85 to-[#0d0b0a]/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b0a]/80 via-transparent to-[#0d0b0a]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-16 w-full">
          <div className="max-w-3xl">
            <span className="reveal inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 backdrop-blur px-4 py-2 text-sm text-white/90 mb-7">
              <Sparkles size={15} className="text-[#22B573]" /> {h.badge}
            </span>
            <h1 className="reveal reveal-delay-1 font-display font-extrabold text-white text-[42px] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight">
              {h.heroTitle1}
              <span className="block text-[#22B573]">{h.heroTitle2}</span>
            </h1>
            <p className="reveal reveal-delay-2 mt-6 text-white/80 text-lg lg:text-xl max-w-xl leading-relaxed">{h.heroSub}</p>
            <div className="reveal reveal-delay-3 mt-9 flex flex-wrap items-center gap-4">
              <Link to={navPaths.innovation} className="btn-press group inline-flex items-center gap-2 rounded-full bg-[#22B573] text-[#083a24] px-7 py-3.5 font-semibold hover:bg-[#1ba065] hover:shadow-xl hover:shadow-emerald-500/25">
                {h.heroCta1}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={navPaths.hotel} className="btn-press inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-7 py-3.5 font-semibold hover:bg-white/10">
                {h.heroCta2}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.3em] uppercase animate-pulse">{t.common.scroll}</div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <span className="text-[#17784A] font-semibold text-sm uppercase tracking-wider">{h.introEyebrow}</span>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl mt-3 tracking-tight text-[#14110F]">{h.introTitle}</h2>
            <div className="mt-6 space-y-5 text-[#3B342E]/90 text-lg leading-relaxed">
              {h.introP.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <Link to={navPaths.about} className="mt-8 inline-flex items-center gap-2 text-[#17784A] font-semibold link-underline">
              {h.introLink} <ArrowRight size={17} />
            </Link>
          </div>
          <div className="reveal reveal-delay-1 relative">
            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-[#22B573]/20 floaty" />
            <div className="grid grid-cols-2 gap-4">
              <img src={images.dish1} alt="" className="rounded-2xl h-56 w-full object-cover shadow-xl card-lift" />
              <img src={images.dish2} alt="" className="rounded-2xl h-56 w-full object-cover shadow-xl mt-8 card-lift" />
              <img src={images.dish3} alt="" className="rounded-2xl h-56 w-full object-cover shadow-xl -mt-4 card-lift" />
              <img src={images.dish4} alt="" className="rounded-2xl h-56 w-full object-cover shadow-xl mt-4 card-lift" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#17784A] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.stats.map((s, i) => (
            <div key={i} className={`reveal reveal-delay-${i} text-center`}>
              <div className="font-display font-extrabold text-4xl lg:text-5xl text-white">{s.value}</div>
              <div className="mt-2 text-white/70 text-sm lg:text-base">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OBJECTIVE */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal order-2 lg:order-1 relative">
            <img src={images.molecular} alt="" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -right-4 lg:right-6 bg-[#FBF9F4] rounded-2xl shadow-xl p-5 flex items-center gap-3 border border-black/5">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#D8382E] text-white"><Award size={24} /></span>
              <div>
                <div className="font-display font-bold text-[#14110F]">{h.objBadgeTitle}</div>
                <div className="text-sm text-[#3B342E]/70">{h.objBadgeSub}</div>
              </div>
            </div>
          </div>
          <div className="reveal reveal-delay-1 order-1 lg:order-2">
            <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">{h.objEyebrow}</span>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl mt-3 tracking-tight text-[#14110F]">{h.objTitle}</h2>
            <p className="mt-6 text-[#3B342E]/90 text-lg leading-relaxed">{h.objText}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[Globe2, Utensils].map((Icon, i) => (
                <div key={i} className="flex gap-3 p-4 rounded-2xl bg-white shadow-sm border border-black/5 card-lift">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#17784A]/10 text-[#17784A] shrink-0"><Icon size={22} /></span>
                  <div>
                    <div className="font-semibold text-[#14110F]">{h.objFeatures[i].t}</div>
                    <div className="text-sm text-[#3B342E]/70">{h.objFeatures[i].d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#F3EFE7] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal max-w-2xl">
            <span className="text-[#17784A] font-semibold text-sm uppercase tracking-wider">{h.procEyebrow}</span>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl mt-3 tracking-tight text-[#14110F]">{h.procTitle}</h2>
            <p className="mt-4 text-[#3B342E]/80 text-lg">{h.procSub}</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} group bg-white rounded-2xl p-7 shadow-sm border border-black/5 card-lift hover:shadow-xl`}>
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#17784A] text-white group-hover:scale-110 transition-transform"><Icon size={24} /></span>
                    <span className="font-display font-extrabold text-3xl text-[#F3EFE7] group-hover:text-[#22B573]/30 transition-colors">{s.step}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-5 text-[#14110F]">{s.title}</h3>
                  <p className="mt-2 text-[#3B342E]/75 text-sm leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
          <div className="reveal mt-12">
            <Link to={navPaths.whatWeDo} className="inline-flex items-center gap-2 text-[#17784A] font-semibold link-underline">
              {h.procLink} <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">{h.chanEyebrow}</span>
          <h2 className="font-display font-extrabold text-4xl lg:text-5xl mt-3 tracking-tight text-[#14110F]">{h.chanTitle}</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.channels.map((c, i) => (
            <div key={i} className={`reveal reveal-delay-${i} relative overflow-hidden rounded-3xl bg-[#14110F] text-white p-8 card-lift`}>
              <span className="inline-block rounded-full bg-[#22B573]/20 text-[#22B573] px-3 py-1 text-xs font-semibold uppercase tracking-wide">{c.tag}</span>
              <h3 className="font-display font-bold text-2xl mt-5">{c.title}</h3>
              <p className="mt-3 text-white/65 leading-relaxed">{c.text}</p>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-[#17784A]/30 blur-2xl" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 lg:pb-32">
        <div className="reveal relative overflow-hidden rounded-[32px] bg-[#17784A] px-8 py-16 lg:p-20 grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tight">{h.ctaTitle}</h2>
            <p className="mt-5 text-white/80 text-lg">{h.ctaText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-white text-[#14110F] px-7 py-3.5 font-semibold hover:bg-[#FBF9F4]">
                {t.common.contactUs} <ArrowRight size={18} />
              </Link>
              <Link to={navPaths.risorice} className="btn-press inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10">
                {t.common.discoverAllf} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
          <ChefHat size={280} className="absolute -right-10 -bottom-16 text-white/5 slowspin" />
        </div>
      </section>
    </main>
  );
}
